// SPDX-License-Identifier: MIT
// Blob listing service (Phase 3)
import { ContainerClient } from '@azure/storage-blob';
import { DataLakeFileSystemClient } from '@azure/storage-file-datalake';
import { getSasUrl } from '../auth/session';
import { AzureListRequest, AzureListPage, AzureBlobItemMeta } from '../types';

export interface BlobListerOptions {
  pageSize?: number;
  rootPrefix?: string;
}
// Keep in sync with PREFIX_RE in utils/config-validation.ts and SAFE_PREFIX_RE in AzureBrowserPane
const SAFE_PREFIX_RE = /^[A-Za-z0-9_\-/]*$/;

export class BlobLister {
  private containerClient?: ContainerClient;
  private dfsClient?: DataLakeFileSystemClient;
  private pageSize: number;
  private mode: 'blob' | 'dfs' = 'blob';
  private baseDir: string = '';
  constructor(opts: BlobListerOptions = {}) {
    const sas = getSasUrl();
    if (!sas) throw new Error('NOT_AUTHENTICATED');
    // Decide mode based on host, sr, and path depth
    try {
      const u = new URL(sas);
      const sr = u.searchParams.get('sr');
      const isDfsHost = /\.dfs\.core\.windows\.net$/i.test(u.hostname);
      const pathSegs = u.pathname.split('/').filter(Boolean);
      const hasDirInPath = pathSegs.length > 1; // <container>/<dir>
      if (isDfsHost || sr === 'd' || hasDirInPath) {
        this.mode = 'dfs';
        const container = pathSegs[0];
        this.baseDir = pathSegs.slice(1).join('/');
        const dfsHost = isDfsHost ? u.hostname : u.hostname.replace('.blob.core.', '.dfs.core.');
        const fsUrl = `https://${dfsHost}/${container}?${u.searchParams.toString()}`;
        this.dfsClient = new DataLakeFileSystemClient(fsUrl);
      } else {
        this.containerClient = new ContainerClient(sas);
      }
    } catch {
      // Fallback to blob mode if parsing fails
      this.containerClient = new ContainerClient(sas);
    }
    this.pageSize = opts.pageSize || 100;
  }

  async list(req: AzureListRequest): Promise<AzureListPage> {
    if (import.meta.env.VITE_E2E_MOCK === '1') {
      const prefix = req.prefix || '';
      if (!prefix) {
        return {
          prefix,
          nextMarker: undefined,
          items: [
            { name: 'sample/', isDirectory: true },
            { name: 'readme.txt', size: 120, isDirectory: false, contentType: 'text/plain' },
          ],
        };
      }
      if (prefix === 'sample/') {
        return {
          prefix,
          nextMarker: undefined,
          items: [
            {
              name: 'sample/coverage.geojson',
              size: 2048,
              isDirectory: false,
              contentType: 'application/geo+json',
            },
            {
              name: 'sample/coverage_table.parquet',
              size: 4096,
              isDirectory: false,
              contentType: 'application/octet-stream',
            },
          ],
        };
      }
    }
    // Normalize and validate prefix defensively (deny-by-default on invalid input)
    // - strip leading slashes to avoid absolute path semantics
    // - only allow safe characters (alnum, underscore, dash, slash)
    const requestedPrefix = (req.prefix || '').replace(/^\/+/, '');
    if (requestedPrefix && !SAFE_PREFIX_RE.test(requestedPrefix)) {
      // Fail closed without leaking details; UI can show empty results for invalid query
      return { items: [], nextMarker: undefined, prefix: '' };
    }
    const prefix = requestedPrefix;
    // Clamp page size defensively: [1, 1000]
    const requestedPageSize = req.pageSize || this.pageSize;
    const clampedPageSize = Math.min(1000, Math.max(1, requestedPageSize));
    if (this.mode === 'dfs' && this.dfsClient) {
      const rawPath = [this.baseDir, prefix].filter(Boolean).join('/').replace(/^\/+/, '');
      // listPaths expects no trailing slash
      const queryPath = rawPath;
      // But UI hierarchy benefits from a trailing slash for prefix math
      const effectivePath = rawPath && !rawPath.endsWith('/') ? `${rawPath}/` : rawPath;
      const byPage: any = (this.dfsClient as any)
        .listPaths({ path: queryPath || undefined, recursive: false })
        .byPage({ continuationToken: req.continuation, maxPageSize: clampedPageSize });
      const { value }: any = await byPage.next();
      const paths: any[] = value?.pathItems || value?.paths || [];
      const items: AzureBlobItemMeta[] = [];
      for (const p of paths) {
        const isDir = Boolean(p.isDirectory);
        const fullName = p.name.endsWith('/') || isDir ? `${p.name.replace(/\/+$/, '')}/` : p.name;
        items.push({
          name: fullName,
          isDirectory: isDir,
          size: isDir ? undefined : p.contentLength,
          contentType: isDir ? undefined : p.contentType,
          lastModified: p.lastModified,
        });
      }
      // Important: return the effective directory as prefix so the UI hierarchy builder
      // treats returned names as children of this path (avoids empty listing).
      return {
        items,
        nextMarker: value?.continuationToken || value?.continuation,
        prefix: effectivePath,
      };
    }
    // Blob (container) mode
    const byPage = this.containerClient!.listBlobsByHierarchy('/', { prefix }).byPage({
      continuationToken: req.continuation,
      maxPageSize: clampedPageSize,
    });
    const { value } = await byPage.next();
    const items: AzureBlobItemMeta[] = [];
    (value.segment.blobPrefixes || []).forEach((d: { name: string }) =>
      items.push({ name: d.name, isDirectory: true })
    );
    interface BlobRaw {
      name: string;
      properties?: { contentLength?: number; contentType?: string; lastModified?: Date };
    }
    (value.segment.blobItems || []).forEach((b: BlobRaw) =>
      items.push({
        name: b.name,
        size: b.properties?.contentLength,
        contentType: b.properties?.contentType,
        lastModified: b.properties?.lastModified,
        isDirectory: false,
      })
    );
    return { items, nextMarker: value.continuationToken, prefix };
  }
}
